/**
 * Cookie Consent Banner
 * Simple GDPR-compliant consent banner for MAGreen
 */

import { useEffect, useState } from 'react';

const CONSENT_KEY = 'magreen_cookie_consent';
const CONSENT_EXPIRY_DAYS = 365;
const SCROLL_THRESHOLD = 300; // Show after scrolling 300px

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent) return;

    // Show banner after scrolling past threshold
    const handleScroll = () => {
      if (window.scrollY > SCROLL_THRESHOLD && !showBanner) {
        setShowBanner(true);
        // Small delay for animation
        setTimeout(() => setIsAnimating(true), 50);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showBanner]);

  const handleAccept = () => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

    const consentData = {
      accepted: true,
      expiryDate: expiryDate.toISOString(),
      acceptedAt: new Date().toISOString()
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));

    // Fade out animation
    setIsAnimating(false);
    setTimeout(() => {
      setShowBanner(false);
    }, 300);
  };

  if (!showBanner) return null;

  return (
    <div
      data-cookie-consent-card
      style={{
        position: 'fixed',
        bottom: '2rem',
        right: '2rem',
        zIndex: 1000,
        maxWidth: '380px',
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '1rem',
        padding: '1.25rem',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(132, 204, 22, 0.2)',
        transform: isAnimating ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        opacity: isAnimating ? 1 : 0,
        transition: 'all 0.3s ease-out'
      }}
    >
      {/* Cookie icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '0.75rem'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          background: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#0a0a0a' }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
          </svg>
        </div>
        <span style={{
          color: '#84cc16',
          fontWeight: 600,
          fontSize: '0.95rem'
        }}>
          Cookies
        </span>
      </div>

      {/* Message */}
      <p style={{
        color: '#e5e7eb',
        fontSize: '0.875rem',
        lineHeight: '1.5',
        marginBottom: '1rem'
      }}>
        Utilizamos cookies para melhorar a sua experiência.
      </p>

      {/* Accept button */}
      <button
        onClick={handleAccept}
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)',
          color: '#0a0a0a',
          border: 'none',
          padding: '0.65rem 1rem',
          borderRadius: '8px',
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(132, 204, 22, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        Aceitar
      </button>

      {/* Mobile responsive */}
      <style>{`
        @media (max-width: 767px) {
          [data-cookie-consent-card] {
            bottom: 1rem !important;
            right: 1rem !important;
            left: 1rem !important;
            max-width: none !important;
          }
        }
      `}</style>
    </div>
  );
}
